import type { AnalyticsEvent } from './types';

/**
 * Events sent in one request. Must not exceed the ingest endpoint's batch cap,
 * which rejects an oversized batch outright rather than truncating it.
 */
const MAX_BATCH_SIZE = 100;

/**
 * Events held before the oldest are dropped.
 *
 * A ceiling is necessary because a user can keep clicking while the network is
 * down, and an unbounded queue would grow until the tab did. Oldest go first:
 * if we can only keep part of a session, the part that explains what the user
 * is doing right now is the useful half.
 */
const MAX_QUEUE_LENGTH = 500;

/** Queue length that triggers a flush without waiting for the timer. */
const FLUSH_THRESHOLD = 20;

export interface AnalyticsQueueOptions {
  /** Ships a batch. Rejects on failure. */
  send: (events: AnalyticsEvent[]) => Promise<void>;
  maxBatchSize?: number;
  maxQueueLength?: number;
  flushThreshold?: number;
}

/**
 * Buffers reported actions and ships them in batches.
 *
 * A dropped batch is never retried. Telemetry competes with the actual
 * application for the same network and the same API, and a producer that
 * retries is a producer that turns a brief outage into a self-inflicted load
 * spike — against the very endpoint that is already struggling. Losing a
 * handful of clicks costs nothing this data is for.
 */
export class AnalyticsQueue {
  private readonly events: AnalyticsEvent[] = [];
  private readonly send: (events: AnalyticsEvent[]) => Promise<void>;
  private readonly maxBatchSize: number;
  private readonly maxQueueLength: number;
  private readonly flushThreshold: number;
  private inFlight = false;
  private droppedCount = 0;

  constructor(options: AnalyticsQueueOptions) {
    this.send = options.send;
    this.maxBatchSize = options.maxBatchSize ?? MAX_BATCH_SIZE;
    this.maxQueueLength = options.maxQueueLength ?? MAX_QUEUE_LENGTH;
    this.flushThreshold = options.flushThreshold ?? FLUSH_THRESHOLD;
  }

  /** How many events are waiting. Exposed for tests and diagnostics. */
  get size(): number {
    return this.events.length;
  }

  /** How many events have been dropped this session, for either reason. */
  get dropped(): number {
    return this.droppedCount;
  }

  /**
   * Add an event, flushing early once enough have piled up.
   *
   * @param event - The action to report.
   */
  enqueue(event: AnalyticsEvent): void {
    this.events.push(event);

    while (this.events.length > this.maxQueueLength) {
      this.events.shift();
      this.droppedCount++;
    }

    if (this.events.length >= this.flushThreshold) {
      void this.flush();
    }
  }

  /**
   * Ship one batch.
   *
   * Concurrent calls are collapsed: the timer, the threshold and a route change
   * can all ask at once, and sending the same events twice would double-count
   * the actions this table exists to count.
   */
  async flush(): Promise<void> {
    if (this.inFlight || this.events.length === 0) return;

    const batch = this.events.splice(0, this.maxBatchSize);
    this.inFlight = true;

    try {
      await this.send(batch);
    } catch {
      // Deliberately swallowed and not requeued — see the class docs.
      this.droppedCount += batch.length;
    } finally {
      this.inFlight = false;
    }
  }
}
